import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import {
  adminCreateVenueOnboardingInvite,
  adminListVenueOnboardingInvites
} from '../../services/venueOnboardingApi';
import './AdminVenueOnboardingInvites.css';

const buildOnboardingUrl = (code) => {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://www.occasia.io';
  return `${origin.replace(/\/$/, '')}/venue-onboarding?invite=${encodeURIComponent(code)}`;
};

const AdminVenueOnboardingInvites = () => {
  const { currentUser } = useAuth();
  const [invites, setInvites] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [lastCreated, setLastCreated] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const rows = await adminListVenueOnboardingInvites(token);
      setInvites(
        (Array.isArray(rows) ? rows : []).map((row) => ({
          ...row,
          onboarding_url: row.used_at ? null : buildOnboardingUrl(row.code)
        }))
      );
    } catch (e) {
      setError(e.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load, currentUser]);

  const onCreate = async () => {
    if (!token || creating) return;
    setCreating(true);
    setError('');
    try {
      const row = await adminCreateVenueOnboardingInvite({ note: note.trim() || null }, token);
      setLastCreated({
        ...row,
        onboarding_url: buildOnboardingUrl(row.code)
      });
      setNote('');
      await load();
    } catch (e) {
      setError(e.message || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy onboarding link:', url);
    }
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="admin-vo-page">
        <div className="admin-vo-inner">
          <Link to="/admin" className="admin-vo-back">
            ← Admin hub
          </Link>
          <h1>Venue onboarding invites</h1>
          <p className="admin-vo-lead">
            Generate one-time codes for venue self-serve signup. Each code expires when a venue is
            created.
          </p>

          <section className="admin-vo-create">
            <label>
              Note for your team (optional)
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Common Press — Aisha"
              />
            </label>
            <button type="button" className="admin-vo-btn" onClick={onCreate} disabled={creating}>
              {creating ? 'Generating…' : 'Generate invite code'}
            </button>
          </section>

          {lastCreated ? (
            <div className="admin-vo-created">
              <p>
                <strong>{lastCreated.code}</strong>
              </p>
              <button type="button" className="admin-vo-link-btn" onClick={() => copyLink(lastCreated.onboarding_url)}>
                Copy onboarding link
              </button>
              <p className="admin-vo-url">{lastCreated.onboarding_url}</p>
            </div>
          ) : null}

          {error ? <p className="admin-vo-error">{error}</p> : null}

          <section className="admin-vo-table-wrap">
            <h2>Recent invites</h2>
            {loading ? <p>Loading…</p> : null}
            {!loading && invites.length === 0 ? <p>No invites yet.</p> : null}
            {!loading && invites.length > 0 ? (
              <table className="admin-vo-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Note</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Venue</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <code>{row.code}</code>
                      </td>
                      <td>{row.note || '—'}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                      <td>{row.used_at ? 'Used' : 'Open'}</td>
                      <td>
                        {row.venue_id ? (
                          <a href={`https://www.occasia.io/venue/${row.venue_id}`} target="_blank" rel="noreferrer">
                            {row.venue_id.slice(0, 8)}…
                          </a>
                        ) : (
                          row.onboarding_url ? (
                            <button type="button" className="admin-vo-link-btn" onClick={() => copyLink(row.onboarding_url)}>
                              Copy link
                            </button>
                          ) : (
                            '—'
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </section>
        </div>
      </main>
    </PageWrapper>
  );
};

export default AdminVenueOnboardingInvites;
